import {useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {ToastType} from '../components/ToastNotification';

interface Notice {
    text: string;
    type?: ToastType;
}

// A page that goes BACK cannot hand its notice over in navigation state:
// history offers no way to write into the entry it returns to. It leaves the
// notice here and the page that shows up next takes it - soon, or never.
const LEFT_FOR_MS = 5000;
let left: { notice: Notice; at: number } | null = null;

export const leaveNotice = (text: string, type: ToastType = 'success'): void => {
    left = {notice: {text, type}, at: Date.now()};
};

const takeLeftNotice = (): Notice | null => {
    const taken = left && Date.now() - left.at < LEFT_FOR_MS ? left.notice : null;
    left = null;
    return taken;
};

/**
 * A workflow that both reports something and sends the user elsewhere cannot
 * show its own toast: the page it was fired from unmounts on the way. The
 * notice therefore travels in navigation state - or, on the way back, through
 * leaveNotice - and is shown here, once, by the page we land on.
 */
export const useIncomingNotice = (showToast: (message: string, type?: ToastType) => void): void => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const notice = (location.state as any)?.notice;
        if (!notice?.text) {
            const leftBehind = takeLeftNotice();
            if (leftBehind) {
                showToast(leftBehind.text, leftBehind.type ?? 'success');
            }
            return;
        }

        showToast(notice.text, notice.type ?? 'success');

        // Drop it from history so a refresh or a back-navigation stays quiet.
        const {notice: _, ...rest} = (location.state as any) ?? {};
        navigate(location.pathname + location.search, {replace: true, state: Object.keys(rest).length ? rest : undefined});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);
};
